// Unified ReviewPanel — single SPA-side shape for every Claude-derived
// "review item" surfaced on a transcript. Item 2 of the 2026-05-14
// architecture extension; replaces the prior split between DriftBanner
// (drift signals on meetings.diarization_flags), FlagPanel (sentinel rows
// in the flags table), and inline-only L3 entries
// (transcripts.span_attributions filtered to source="L3").
//
// Storage stays distributed for v0 (Decision 1 of DECISIONS.md 2026-05-14
// "Semantic review surface + context-enrichment architecture"). The SPA
// reads each source as it already does and aggregates into the
// ReviewItem[] shape at render time. Future detectors (role-inversion,
// interloper, attendee-inconsistency in Items 6-8) add new `kind` values
// + write paths but inherit the panel shape automatically.
//
// Per-item status persistence:
//   - Sentinel flags: `flags.status` column (migration 0010 + earlier).
//   - Drift entries (jsonb in meetings.diarization_flags): the SPA
//     adds an optional `status` field to each entry via read-modify-write
//     when the user acts on the row. Backend's drift_detector.py never
//     writes status; pending is the default.
//   - L3 entries (jsonb in transcripts.span_attributions with source="L3"):
//     same read-modify-write pattern, status added by the SPA.
//
// Manual span_attributions (source="manual") are NOT surfaced in the
// panel -- they're already the user's truth, not a Claude proposal
// awaiting review. The cascade renders them; the panel skips them.

import { Flag } from "./flags";
import { SpanAttribution } from "./corrections";
import { supabase } from "./supabase";

// ---------------------------------------------------------------------------
// Drift entry shape (from worker drift_detector.py output, stored in
// meetings.diarization_flags jsonb). Inlined here when DriftBanner.tsx
// was deleted in the Item 2 unification slice.
// ---------------------------------------------------------------------------

export interface DriftFlag {
  signal: "anchor_label" | "no_overlap_handoff" | "anomalous_label_count";
  severity: "medium" | "high";
  detail: string;
  start_ms: number | null;
  end_ms: number | null;
  label: string | null;
  evidence: Record<string, unknown>;
  // SPA-side per-entry status augmentation. Optional -- backend never
  // writes this; SPA adds it via read-modify-write on Accept/Dismiss.
  status?: ReviewItemStatus;
}

// ---------------------------------------------------------------------------
// Unified types
// ---------------------------------------------------------------------------

export type ReviewItemStatus =
  | "pending"
  | "accepted"
  | "dismissed"
  | "corrected"; // text-flag-specific terminal

export type ReviewItemSource = "sentinel" | "claude_speaker";

export type ReviewItemKind =
  // Sentinel text kinds (DECISIONS.md 2026-05-13 "Hallucination sentinel")
  | "proper_noun_oov"
  | "lost_thread"
  | "silence_fill"
  | "code_switch_break"
  | "loop_repetition"
  // Semantic-coherence flag (DECISIONS.md 2026-05-29 "Group B"). Passages
  // whose content is clearly discordant with the meeting context — the
  // overlap-confusion signature where Soniox picks up a second voice.
  // User-facing label: "May not fit the conversation".
  | "out_of_context_passage"
  // Claude-proposed speaker re-attribution (DECISIONS.md 2026-05-14 "L3")
  // - claude_speaker_proposal: Claude proposed a name with confidence
  // - claude_speaker_uncertain: anomaly-only (placeholder for the
  //   Decision-1 anomaly-without-suggestion mode + future detectors)
  | "claude_speaker_proposal"
  | "claude_speaker_uncertain"
  // Role-inversion (DECISIONS.md 2026-05-17 "Role-inversion detector").
  // Spans where the labeled speaker's content semantically belongs to
  // a different speaker's inferred role. Catches cough-swap-style
  // confusion from a third angle, independent of drift (structural)
  // and L3 (exemplar-based).
  | "role_inversion"
  // Attribution second-look (migration 0019). Written by the
  // attribution_critique Claude pass; flags spans where the speaker
  // assignment may need human review after cross-referencing context.
  | "attribution_second_look";

export type ReviewItemSeverity = "low" | "medium" | "high";

export interface ReviewItem {
  // Composite id: `<source>:<inner-id-or-index>`. Stable across renders
  // so React key + the in-flight optimistic-update map don't reshuffle.
  id: string;
  source: ReviewItemSource;
  kind: ReviewItemKind;
  severity: ReviewItemSeverity;
  status: ReviewItemStatus;
  // start_ms can be null for drift's anomalous_label_count signal
  // (whole-meeting observation with no specific moment).
  start_ms: number | null;
  end_ms: number | null;
  // One-line summary rendered in the row. Detector-provided when
  // available (sentinel `reason`, drift `detail`, L3 `reasoning`).
  detail: string;
  // Open-the-hood raw payload toggled via the row's evidence button.
  evidence: Record<string, unknown>;
  // Original source row -- the ReviewItemRow uses this to write status
  // back to the correct storage layer. Discriminated union by source.
  origin:
    | { source: "sentinel"; flag: Flag }
    | { source: "claude_speaker"; entry: SpanAttribution };
  // Derived/source fields for the act-or-dismiss action model (2026-06-06).
  suggestedName: string | null;       // concrete speaker to one-click apply (second-look evidence.suggested_name or L3 name); null = nothing to apply
  rawLabelAtCreation: string | null;  // Soniox label this span was authored against — needed to build a matching manual span on apply
  appliedSpan: SpanAttribution | null; // covering manual span (if any) => item is "Applied"; derived, never written
}

// ---------------------------------------------------------------------------
// Kind + severity vocabulary
// ---------------------------------------------------------------------------

// Plain-language labels for the panel row. UI-copy convention pinned
// 2026-05-14 (DECISIONS.md "UI copy conventions" entry): user-facing
// surfaces use plain language; internal codenames (L1/L2/L3) stay in
// code. "Claude suggestion" replaces "L3" in row text + chrome; the
// internal storage source remains "L3".
export const KIND_LABEL: Record<ReviewItemKind, string> = {
  // sentinel
  proper_noun_oov: "Proper noun out-of-vocab",
  lost_thread: "Lost thread",
  silence_fill: "Silence fill",
  code_switch_break: "Code-switch break",
  loop_repetition: "Loop repetition",
  // Group B — semantic-coherence flag. Plain language per feedback_plain_language_ui:
  // "a non-coder should immediately understand what this flag means without docs."
  out_of_context_passage: "May not fit the conversation",
  // claude_speaker
  claude_speaker_proposal: "Claude speaker suggestion",
  claude_speaker_uncertain: "Speaker anomaly",
  // role-inversion
  role_inversion: "Speaker role inversion",
  // attribution second-look
  attribution_second_look: "Attribution: second-look",
};

// Severity -> CSS color token. Mirrors the prior flags.ts mapping for
// continuity (low = muted; medium = amber; high = red). DriftBanner used
// to elide low; the unified panel keeps three tiers so drift signals
// (medium/high) and future low-severity detectors share the vocabulary.
export const SEVERITY_COLOR: Record<ReviewItemSeverity, string> = {
  low: "var(--text-muted)",
  medium: "var(--warning, #b45309)",
  high: "var(--danger, #b91c1c)",
};

// Severity ranking for ordering. Higher = more urgent.
const SEVERITY_RANK: Record<ReviewItemSeverity, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

// Severity-desc, then chronological-asc within tier (design-decisions
// Q2 (a) — matches time-to-lock). Items with null start_ms (drift's
// whole-meeting signals) sort first within their severity tier.
export function compareReviewItems(a: ReviewItem, b: ReviewItem): number {
  const sevDiff = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
  if (sevDiff !== 0) return sevDiff;
  const aStart = a.start_ms ?? -1;
  const bStart = b.start_ms ?? -1;
  return aStart - bStart;
}

// Outer-border tint follows the highest-severity active (non-dismissed)
// item. Empty / all-dismissed defaults to the low tint -- the panel's
// visible border still reads as "review surface" even when everything's
// resolved.
export function aggregateSeverityTint(items: ReviewItem[]): string {
  const active = items.filter((i) => i.status !== "dismissed");
  if (active.some((i) => i.severity === "high")) return SEVERITY_COLOR.high;
  if (active.some((i) => i.severity === "medium")) return SEVERITY_COLOR.medium;
  return SEVERITY_COLOR.low;
}

// ---------------------------------------------------------------------------
// Aggregation: three sources -> ReviewItem[]
// ---------------------------------------------------------------------------

// Returns the manual span that exactly applies a review item's suggestion,
// or null. Only source="manual" counts — auto/L3 spans are Claude's
// attribution, not a user action, so they must NOT mark an item "Applied".
export function appliedManualSpanFor(
  startMs: number,
  endMs: number,
  rawLabel: string | null,
  spanAttributions: SpanAttribution[],
): SpanAttribution | null {
  for (const s of spanAttributions) {
    if (s.source !== "manual") continue;
    // EXACT-range match (not overlap): "use <name>" writes a manual span at
    // the item's exact [start_ms, end_ms], so an exact match precisely
    // identifies "this item's suggestion was applied" without false-matching
    // an unrelated rename or a sibling flag. Aligns with the apply write.
    if (s.start_ms !== startMs || s.end_ms !== endMs) continue;
    // Strict label equality on normalized-empty values: a name-only flag
    // (rawLabel null) only matches a span whose label is also empty.
    if ((s.raw_label_at_creation ?? "") !== (rawLabel ?? "")) continue;
    return s;
  }
  return null;
}

// Build the panel's items array from sentinel flags + Claude speaker
// proposals. Drift signals (meetings.diarization_flags) are excluded from
// the panel per the 2026-05-25 annotation pipeline redesign spec: they are
// prompt-input to the attribution Claude call and audit log only.
// Called from MeetingReview render; pure function, safe to call on every
// render (item count is small -- typical meetings have <20 items total).
export function aggregateReviewItems(
  flags: Flag[],
  spanAttributions: SpanAttribution[],
): ReviewItem[] {
  const out: ReviewItem[] = [];

  for (const f of flags) {
    const rawLabel = typeof f.evidence?.raw_label_at_creation === "string" ? (f.evidence.raw_label_at_creation as string) : null;
    const suggested = typeof f.evidence?.suggested_name === "string" ? (f.evidence.suggested_name as string) : null;
    // Only speaker-applyable kinds can show "Applied: <speaker>". A text flag
    // has no speaker semantics; deriving appliedSpan from a coincidental
    // manual rename would both mislabel it and let "undo" delete the user's
    // unrelated rename. Text kinds get null.
    const speakerApplyable = f.kind === "attribution_second_look" || f.kind === "role_inversion";
    out.push({
      id: `sentinel:${f.id}`,
      source: "sentinel",
      kind: f.kind as ReviewItemKind,
      severity: f.severity,
      status: f.status,
      start_ms: f.start_ms,
      end_ms: f.end_ms,
      detail: f.reason,
      evidence: f.evidence,
      origin: { source: "sentinel", flag: f },
      suggestedName: suggested,
      rawLabelAtCreation: rawLabel,
      appliedSpan: speakerApplyable ? appliedManualSpanFor(f.start_ms, f.end_ms, rawLabel, spanAttributions) : null,
    });
  }

  // L3 entries from span_attributions. Manual entries are skipped --
  // they're the user's ground truth, not awaiting review. The
  // `confidence` field is L3-only, no confidence threshold yet (see
  // IDEAS.md "Hybrid-by-confidence L3 acceptance" — promote when L3
  // visibly over-proposes on real meetings).
  for (const s of spanAttributions) {
    if (s.source !== "L3") continue;
    // Severity heuristic for v0: claude_speaker_proposal items are
    // medium by default. high if confidence < 0.5 (low confidence =
    // higher review urgency, NOT lower); low if confidence >= 0.85.
    // Tunable; the panel's ordering uses this same severity.
    const conf = s.confidence ?? 0.7;
    const severity: ReviewItemSeverity =
      conf < 0.5 ? "high" : conf >= 0.85 ? "low" : "medium";
    const status =
      (s as SpanAttribution & { status?: ReviewItemStatus }).status ?? "pending";
    out.push({
      id: `claude_speaker:${s.id}`,
      source: "claude_speaker",
      kind: "claude_speaker_proposal",
      severity,
      status,
      start_ms: s.start_ms,
      end_ms: s.end_ms,
      detail: s.reasoning
        ? `Suggested name: ${s.name}. ${s.reasoning}`
        : `Suggested name: ${s.name}.`,
      evidence: {
        name: s.name,
        confidence: s.confidence ?? null,
        reasoning: s.reasoning ?? null,
        raw_label_at_creation: s.raw_label_at_creation,
        created_at: s.created_at,
      },
      origin: { source: "claude_speaker", entry: s },
      suggestedName: s.name ?? null,
      rawLabelAtCreation: s.raw_label_at_creation ?? null,
      appliedSpan: appliedManualSpanFor(s.start_ms, s.end_ms, s.raw_label_at_creation ?? null, spanAttributions),
    });
  }

  return out;
}

// Active = not dismissed. Drives the severity breakdown + the tint;
// dismissed items render in the row list but greyed out.
export function summarizeSeverities(items: ReviewItem[]): string {
  const active = items.filter((i) => i.status !== "dismissed");
  const high = active.filter((i) => i.severity === "high").length;
  const med = active.filter((i) => i.severity === "medium").length;
  const low = active.filter((i) => i.severity === "low").length;
  const parts: string[] = [];
  if (high > 0) parts.push(`${high} high`);
  if (med > 0) parts.push(`${med} medium`);
  if (low > 0) parts.push(`${low} low`);
  return parts.join(", ");
}

// ---------------------------------------------------------------------------
// Writers
// ---------------------------------------------------------------------------

// Set the panel review status on a single drift entry inside
// meetings.diarization_flags. Read-modify-write; identifies the entry by
// composite key (signal + start_ms + label) since backend
// drift_detector.py doesn't emit per-entry ids. After a Re-annotate the
// worker overwrites the whole array, which deliberately drops prior
// Accept/Dismiss state -- re-annotation invalidates the prior
// observation; we treat the new output as fresh.
export async function setDiarizationFlagStatus(
  meetingId: string,
  signal: DriftFlag["signal"],
  startMs: number | null,
  label: string | null,
  status: ReviewItemStatus,
): Promise<void> {
  const { data: row, error: readErr } = await supabase
    .from("meetings")
    .select("diarization_flags")
    .eq("id", meetingId)
    .maybeSingle();
  if (readErr) throw readErr;
  const current = ((row?.diarization_flags ?? []) as DriftFlag[]).map((e) => {
    if (
      e.signal === signal &&
      (e.start_ms ?? null) === (startMs ?? null) &&
      (e.label ?? null) === (label ?? null)
    ) {
      return { ...e, status };
    }
    return e;
  });
  const { error: writeErr } = await supabase
    .from("meetings")
    .update({ diarization_flags: current })
    .eq("id", meetingId);
  if (writeErr) throw writeErr;
}

// Re-export the underlying types so callers can keep importing from
// reviewItems.ts when they need the full picture.
export type { Flag, FlagKind, FlagSeverity } from "./flags";
